import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { CheckBox, ColorType, Skeleton } from '..';
import { useAuth, useWindowSize } from '../../../hooks';
import { Button, ButtonSize, ButtonType } from '../Button.component';
import { Icon } from '../Icon.component';
import { Label, LabelType } from '../Label.component';
import { Grid } from '../grid/Grid.component';
import { useGlobalModalContext } from './GlobalModal.component';

import { ChevronLeft, ExitDeleteRemoveClose } from '../../../assets/icons';
import {
  useAcceptPersonalInvitesMutation,
  useDeclinePersonalInvitesMutation,
  useFetchPersonalInvitesQuery,
} from '../../../services/personalinvite.api';
import { PersonalInvite } from '../../../stores/types/invites.interface';
import { Form } from '../Form.component';
import { MODAL_LAYER_INDEX } from './utils';

const ROW_PER_PAGE = 10;

const AcceptInviteModal = () => {
  const [selectedInvites, setSelectedInvites] = useState<PersonalInvite[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isIgnore, setIsIgnore] = useState(false);
  const auth = useAuth();
  const { hideModal, store } = useGlobalModalContext();
  const windowSize = useWindowSize();
  const { modalProps } = store;
  const { t } = useTranslation();
  const {
    width = '540px',
    height = 'max-content',
    overflow = 'auto',
    shouldCloseOnOverlayClick = true,
  } = modalProps;

  const { data: invites, isLoading: loading } = useFetchPersonalInvitesQuery();
  const [acceptInvites, acceptQueryResult] = useAcceptPersonalInvitesMutation();
  const [declineInvites, declineQueryResult] = useDeclinePersonalInvitesMutation();

  const companyIdFromLocalStorage = auth.user.attributes.profile?.lastActiveCompanyId;

  const sortedInvites = useMemo(() => {
    return invites
      ? [...invites].sort((a, b) => a.dateRequested.getTime() - b.dateRequested.getTime())
      : [];
  }, [invites]);

  const invitesOnCurrentPage = useMemo(() => {
    if (sortedInvites.length === 0) {
      return [];
    }

    const startIndex = (currentPage - 1) * ROW_PER_PAGE;
    return sortedInvites.slice(startIndex, startIndex + ROW_PER_PAGE);
  }, [currentPage, sortedInvites]);

  const handleClose = () => {
    hideModal();
  };

  useEffect(() => {
    const navigateToCompany = async () => {
      const companyId = companyIdFromLocalStorage || selectedInvites[0].companyId;

      await auth.updateCompanyId(companyId);

      window.location.replace(`/?companyId=${companyId}`);
    };

    if (acceptQueryResult.isSuccess) {
      navigateToCompany();
      hideModal();
    }

    if (declineQueryResult.isSuccess) {
      hideModal();
    }
  }, [
    auth,
    companyIdFromLocalStorage,
    selectedInvites,
    acceptQueryResult.isSuccess,
    declineQueryResult.isSuccess,
    hideModal,
  ]);

  const handleAcceptInvites = async () => {
    await acceptInvites(selectedInvites.map((item) => item.id)).unwrap();

    // Clear company from localStorage at this point so we dont try to re-query it
    localStorage.removeItem('companyId');

    // Wait 2 seconds before proceeding
    // button loading state will still be showing to user
    await new Promise((resolve) => {
      setTimeout(resolve, 2000);
    });
  };

  const handleDeleteInvites = () => {
    declineInvites(selectedInvites.map((item) => item.id));
  };

  const isAllCurrentPageSelected = useMemo(() => {
    return (
      invitesOnCurrentPage.length > 0 && selectedInvites.length === invitesOnCurrentPage.length
    );
  }, [selectedInvites, invitesOnCurrentPage]);

  const handleCheckboxSelection = (checked: boolean, row: PersonalInvite) => {
    if (checked) {
      setSelectedInvites([...selectedInvites, row]);
    } else {
      setSelectedInvites(selectedInvites.filter((invite) => invite.id !== row.id));
    }
  };

  const modalMaxHeight = useMemo(() => {
    if (windowSize.height === 0) {
      return 640;
    }
    return windowSize.height - 80;
  }, [windowSize.height]);

  const handleAllSelected = (selected: boolean) => {
    if (selected) {
      setSelectedInvites(_.uniqBy([...selectedInvites, ...invitesOnCurrentPage], 'companyId'));
    } else {
      setSelectedInvites(_.differenceBy(selectedInvites, invitesOnCurrentPage, 'companyId'));
    }
  };

  const invitedDesc =
    sortedInvites.length > 0 ? 'received_invite_desc' : 'dont_have_company_invite';

  const headersubTitle = isIgnore ? 'delete_selected_company' : invitedDesc;

  const renderHeader = () => {
    return (
      <>
        <div className='flex items-center justify-center sticky top-0 h-16 bg-white'>
          {isIgnore ? (
            <Icon
              key='previcon'
              src={ChevronLeft}
              className='absolute left-2 cursor-pointer'
              onClick={() => setIsIgnore(false)}
            />
          ) : (
            <Icon
              key='closicon'
              src={ExitDeleteRemoveClose}
              className='absolute left-2 cursor-pointer'
              onClick={handleClose}
            />
          )}
          <Label text={t('company_invites')} type={LabelType.H5} color={ColorType.BLACK} />
        </div>
        <div className='flex items-center top-0'>
          <Label
            text={t(headersubTitle, {
              companyNumber: selectedInvites?.length,
            })}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        </div>
      </>
    );
  };

  const renderBody = () => {
    return (
      sortedInvites.length > 0 &&
      !isIgnore && (
        <div className='mt-[16px]'>
          <Grid
            pageIndex={currentPage}
            loadPage={(page: number) => setCurrentPage(page)}
            columns={[
              {
                key: 'companyName',
                title: (
                  <CheckBox
                    label={t('company')}
                    onChange={handleAllSelected}
                    selected={isAllCurrentPageSelected}
                  />
                ),
                component: (row: PersonalInvite) =>
                  loading ? (
                    <Skeleton height='h-5' />
                  ) : (
                    <CheckBox
                      label={row.companyName}
                      onChange={(checked: boolean) => handleCheckboxSelection(checked, row)}
                      selected={
                        selectedInvites.findIndex((invite) => invite.companyId === row.companyId) >
                        -1
                      }
                    />
                  ),
              },
              {
                key: 'dateRequested',
                title: t('date_invited'),
                component: (row: PersonalInvite) =>
                  loading ? (
                    <Skeleton height='h-5' />
                  ) : (
                    <Label
                      text={row.dateRequested.toLocaleDateString()}
                      type={LabelType.BODY3}
                      color={ColorType.BLACK}
                    />
                  ),
              },
            ]}
            totalPage={Math.ceil((sortedInvites?.length || 0) / ROW_PER_PAGE)}
            data={invitesOnCurrentPage}
            primaryKey='name'
            isLoading={loading}
            showPageSearch={false}
          />
        </div>
      )
    );
  };
  const renderFooter = () => {
    return (
      <div className='pt-6 pb-5 bottom-0 sticky bg-white'>
        <div className='h-12 w-auto flex'>
          {sortedInvites.length > 0 ? (
            <>
              <Button
                className='mr-1.5'
                label={t('ignore_selected')}
                type={ButtonType.TERTIARY}
                disabled={selectedInvites?.length === 0}
                size={ButtonSize.MEDIUM_FULL}
                onClick={() => setIsIgnore(true)}
              />
              <Form
                className='w-full'
                onSubmit={handleAcceptInvites}
                queryResult={acceptQueryResult}
              >
                <Button
                  className='ml-1.5'
                  label={t('accept_selected')}
                  type={ButtonType.PRIMARY}
                  disabled={selectedInvites?.length === 0}
                  size={ButtonSize.MEDIUM_FULL}
                  isSumbit
                />
              </Form>
            </>
          ) : (
            <Button
              label={t('ok')}
              type={ButtonType.PRIMARY}
              size={ButtonSize.MEDIUM_FULL}
              onClick={handleClose}
            />
          )}
        </div>
      </div>
    );
  };

  const renderIgnoreFooter = () => {
    return (
      <div className='pt-6 pb-5 bottom-0 sticky bg-white'>
        <div className='h-12 w-auto flex'>
          <Button
            className='mr-1.5'
            label={t('back')}
            type={ButtonType.TERTIARY}
            size={ButtonSize.MEDIUM_FULL}
            onClick={() => setIsIgnore(false)}
            disabled={selectedInvites?.length < 1}
          />
          <Form className='w-full' onSubmit={handleDeleteInvites} queryResult={declineQueryResult}>
            <Button
              className='ml-1.5'
              label={t('ignore')}
              type={ButtonType.PRIMARY}
              size={ButtonSize.MEDIUM_FULL}
              isSumbit
              disabled={selectedInvites?.length < 1}
            />
          </Form>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen
      onRequestClose={handleClose}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      style={{
        content: {
          overflow,
          top: '50%',
          left: '50%',
          width,
          height,
          maxHeight: modalMaxHeight,
          transform: 'translate(-50%, -50%)',
          padding: 0,
          borderRadius: 10,
        },
        overlay: {
          zIndex: MODAL_LAYER_INDEX,
          backgroundColor: 'rgba(32,34,35,0.5)',
        },
      }}
    >
      <div className='flex flex-col px-4'>
        {renderHeader()}
        {renderBody()}
        {isIgnore ? renderIgnoreFooter() : renderFooter()}
      </div>
    </Modal>
  );
};

export default AcceptInviteModal;
